import { v4 as uuidv4 } from 'uuid';
import { fileUpload } from 'features/common/file-request';
import {
  ACK_DOCUMENT_UPLOAD,
  AckDocument,
  AckDocumentAssignment,
  AckDocumentAssignmentsQuery,
  AckDocumentCounters,
  AckDocumentCreatePayload,
  AckDocumentPager,
  AckDocumentResult,
} from 'features/ack-documents/types';
import { tGlobal } from 'features/ack-documents/utils/translate';
import { request } from '~/utils/request';

interface AckDocumentErrorBody {
  result?: {
    type?: string,
    message?: string,
    errors?: unknown,
  },
}

interface AckDocumentListResponse<T> {
  list: T[],
  pager?: AckDocumentPager,
}

/** Подробные логи только в dev-сборке, предупреждения и ошибки — всегда */
export const logAckInfo = (method: string, message: string, data?: unknown) => {
  if (import.meta.env.DEV) {
    console.info(`[AckDocuments.${method}] ${message}`, data ?? '');
  }
};

/** Бэк отдаёт ошибки полей либо плоско в errors, либо в errors.fields — поддерживаем оба вида */
const extractFields = (errors: unknown): Record<string, string> => {
  if (!errors || typeof errors !== 'object') {
    return {};
  }
  const nested = (errors as { fields?: Record<string, string> }).fields;
  if (nested && typeof nested === 'object') {
    return nested;
  }

  return Object.fromEntries(
    Object.entries(errors as Record<string, unknown>)
      .filter(([key, value]) => key !== 'root' && typeof value === 'string'),
  ) as Record<string, string>;
};

/**
 * Запросы идут через api.instance, поэтому глобальный interceptError не показывает тосты:
 * ошибки выводятся inline в модалке и на странице документа.
 */
const parseAckError = <T>(method: string, e: { status?: number, text?: string }): AckDocumentResult<T> => {
  // Вызывается из .catch запроса, вне setup — поэтому tGlobal, а не useTranslate
  const fallback = tGlobal('hubstr.ack_documents.errors.common');

  let body: AckDocumentErrorBody = {};
  try {
    body = JSON.parse(e?.text ?? '{}');
  } catch (parseErr) {
    console.error(`[AckDocuments.${method}] unparsable error response`, { status: e?.status, parseErr });
  }

  const fields = extractFields(body.result?.errors);
  const message = body.result?.message || Object.values(fields).join(' ') || fallback;

  console.warn(`[AckDocuments.${method}] request failed`, {
    status: e?.status,
    type: body.result?.type,
    message,
    fields: Object.keys(fields),
  });

  return { error: message, fields, status: e?.status };
};

/**
 * Счётчики из ответа create. Бэк считает их по аудитории, а list и get — по таблице назначений,
 * которую заполняет очередь. Пока очередь не отработала, list и get отдают assigned = 0:
 * подставляем счётчики из create, а когда бэк вернёт реальные назначения — забываем их.
 */
const createdCounters = new Map<string, AckDocumentCounters>();

const withCreatedCounters = (method: string, document: AckDocument): AckDocument => {
  const cached = createdCounters.get(document.id);
  if (!cached) {
    return document;
  }

  if ((document.counters?.assigned ?? 0) > 0) {
    createdCounters.delete(document.id);
    logAckInfo(method, 'assignments delivered, cached counters dropped', {
      id: document.id, counters: document.counters,
    });
    return document;
  }

  logAckInfo(method, 'assignments pending, using counters from create', { id: document.id, counters: cached });
  return { ...document, counters: cached, countersPending: true };
};

const handleDocument = (method: string) => (response: unknown): AckDocumentResult<AckDocument> => {
  const document = (response as { document?: AckDocument })?.document;
  if (!document) {
    console.error(`[AckDocuments.${method}] unexpected response`, response);
    return { error: 'Некорректный ответ сервера' };
  }

  logAckInfo(method, 'ok', { id: document.id, counters: document.counters });

  if (method === 'create') {
    if ((document.counters?.assigned ?? 0) > 0) {
      createdCounters.set(document.id, document.counters);
    }
    return { data: { ...document, countersPending: true } };
  }

  return { data: withCreatedCounters(method, document) };
};

/** Таблица документов (Magner). Поиск — по заголовку документа */
export const ackDocumentsRead = request.table<AckDocument>(async ({ api, data }) => {
  const page = data.pagination?.page || 1;
  const count = data.pagination?.items || 25;
  const search = String(data.filters?.search ?? '').trim();

  let query = `?page=${page}&count=${count}`;
  if (search) {
    query += `&filters[0][id]=search&filters[0][value]=${encodeURIComponent(search)}`;
  }

  logAckInfo('list', 'start', { page, count, hasSearch: !!search });

  // api.instance, а не api.get: при выключенной фиче бэк отвечает 404, глобальный тост тут не нужен
  const res: AckDocumentResult<AckDocumentListResponse<AckDocument>> = await api
    .instance
    .url(`/api/admin/ack-documents/list${query}`)
    .get()
    .json((response: unknown) => ({ data: response as AckDocumentListResponse<AckDocument> }))
    .catch((e) => {
      if (e?.status === 404) {
        console.warn('[AckDocuments.list] feature is disabled for the club (404)');
        return { status: 404 };
      }
      return parseAckError('list', e);
    });

  if (!res.data || !Array.isArray(res.data.list)) {
    if (!res.status && !res.error) {
      console.error('[AckDocuments.list] unexpected response', res);
    }
    return {
      data: {
        rows: [],
        pagination: null,
      },
    };
  }

  const rows = res.data.list.map((document) => withCreatedCounters('list', document));
  logAckInfo('list', 'ok', { rows: rows.length, pager: res.data.pager });

  return {
    data: {
      rows,
      pagination: {
        currentPage: res.data.pager?.currentPage || 1,
        totalPages: res.data.pager?.totalPages || 1,
        totalItems: res.data.pager?.totalItems || 0,
      },
    },
  };
});

export const ackDocumentGet = (id: string): Promise<AckDocumentResult<AckDocument>> => {
  logAckInfo('get', 'start', { id });

  const get = request.custom<AckDocumentResult<AckDocument>>(async ({ api }) => await api
    .instance
    .url(`/api/admin/ack-documents/get?id=${encodeURIComponent(id)}`)
    .get()
    .json(handleDocument('get'))
    .catch((e) => parseAckError<AckDocument>('get', e)) as any);

  return get({}) as unknown as Promise<AckDocumentResult<AckDocument>>;
};

export const ackDocumentAssignments = (
  id: string,
  query: AckDocumentAssignmentsQuery,
): Promise<AckDocumentResult<AckDocumentListResponse<AckDocumentAssignment>>> => {
  const search = query.search.trim();
  logAckInfo('assignments', 'start', {
    id, page: query.page, count: query.count, status: query.status || 'all', hasSearch: !!search,
  });

  let url = `/api/admin/ack-documents/assignments?id=${encodeURIComponent(id)}&page=${query.page}&count=${query.count}`;
  let filterIndex = 0;
  if (query.status) {
    url += `&filters[${filterIndex}][id]=status&filters[${filterIndex}][value]=${query.status}`;
    filterIndex += 1;
  }
  if (search) {
    url += `&filters[${filterIndex}][id]=search&filters[${filterIndex}][value]=${encodeURIComponent(search)}`;
  }

  const assignments = request.custom<AckDocumentResult<AckDocumentListResponse<AckDocumentAssignment>>>(
    async ({ api }) => await api
      .instance
      .url(url)
      .get()
      .json((response: unknown) => {
        const data = response as AckDocumentListResponse<AckDocumentAssignment>;
        if (!Array.isArray(data?.list)) {
          console.error('[AckDocuments.assignments] unexpected response', response);
          return { error: 'Некорректный ответ сервера' };
        }
        logAckInfo('assignments', 'ok', { id, rows: data.list.length, pager: data.pager });
        return { data };
      })
      .catch((e) => parseAckError('assignments', e)) as any,
  );

  return assignments({}) as unknown as Promise<AckDocumentResult<AckDocumentListResponse<AckDocumentAssignment>>>;
};

export const ackDocumentCreate = (payload: AckDocumentCreatePayload): Promise<AckDocumentResult<AckDocument>> => {
  logAckInfo('create', 'start', {
    fileId: payload.fileId,
    audience: payload.criteria.length ? 'labels' : 'all',
    tagsCount: payload.criteria[0]?.tagIds.length ?? 0,
  });

  const create = request.custom<AckDocumentResult<AckDocument>>(async ({ api }) => await api
    .instance
    .url('/api/admin/ack-documents/create')
    .post(payload)
    .json(handleDocument('create'))
    .catch((e) => parseAckError<AckDocument>('create', e)) as any);

  return create({}) as unknown as Promise<AckDocumentResult<AckDocument>>;
};

export const ackDocumentDelete = (id: string): Promise<AckDocumentResult<true>> => {
  logAckInfo('delete', 'start', { id });

  const remove = request.custom<AckDocumentResult<true>>(async ({ api }) => await api
    .instance
    .url(`/api/admin/ack-documents/delete?id=${encodeURIComponent(id)}`)
    .delete()
    .res(() => {
      logAckInfo('delete', 'ok', { id });
      return { data: true };
    })
    .catch((e) => parseAckError<true>('delete', e)) as any);

  return remove({}) as unknown as Promise<AckDocumentResult<true>>;
};

/** glId — любой UUID: документа ещё нет */
export const ackDocumentUploadPdf = async (file: File): Promise<AckDocumentResult<string>> => {
  logAckInfo('upload', 'start', { name: file.name, size: file.size, type: file.type });

  const res = await fileUpload(
    uuidv4(),
    file,
    ACK_DOCUMENT_UPLOAD.glType,
    ACK_DOCUMENT_UPLOAD.code,
    ACK_DOCUMENT_UPLOAD.context,
  );

  if (res.error || !res.data?.id) {
    console.warn('[AckDocuments.upload] failed', { error: res.error });
    return { error: (res.error as unknown as string) || 'Не удалось загрузить файл' };
  }

  logAckInfo('upload', 'ok', { fileId: res.data.id });
  return { data: res.data.id };
};
